<template>
  <div class="tab-content app-management">
    <div class="main-header">
      <div>
        <h1>{{ $t('controlCenter.appManagement.title') }}</h1>
        <p class="subtitle">{{ $t('controlCenter.appManagement.subtitle') }}</p>
      </div>
      <button class="btn btn-primary" @click="fetchApps" :disabled="loading">
        <i class="fas fa-rotate-right"></i>
        {{ $t('controlCenter.common.refresh') }}
      </button>
    </div>

    <div v-loading="loading" class="app-sections">
      <!-- 已开通应用 -->
      <div class="app-section">
        <h2 class="section-title">{{ $t('controlCenter.appManagement.section.active') }}</h2>
        <div v-if="activeApps.length === 0" class="empty-section">
          <p>{{ $t('controlCenter.appManagement.empty') }}</p>
        </div>
        <div v-else class="app-list">
          <div
            v-for="(app, index) in activeApps"
            :key="app.appId"
            class="app-item"
            :class="{ 'border-top': index > 0 }"
          >
            <div class="app-icon-wrapper">
              <img :src="appIcon" :alt="app.appName" class="app-icon-img" />
            </div>
            <div class="app-info">
              <div class="app-name" :title="app.appName">{{ app.appName || '-' }}</div>
            </div>
            <div class="app-actions">
              <button
                class="action-btn suspend"
                @click="handleSuspend(app)"
                :disabled="actionLoading === app.appId"
              >
                {{ $t('controlCenter.appManagement.actions.suspend') }}
              </button>
              <button
                class="action-btn deprovision"
                @click="handleDeprovision(app)"
                :disabled="actionLoading === app.appId"
              >
                {{ $t('controlCenter.appManagement.actions.deprovision') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Suspended 应用 -->
      <div class="app-section">
        <h2 class="section-title">{{ $t('controlCenter.appManagement.section.suspend') }}</h2>
        <div v-if="pausedApps.length === 0" class="empty-section">
          <p>{{ $t('controlCenter.appManagement.empty') }}</p>
        </div>
        <div v-else class="app-list">
          <div
            v-for="(app, index) in pausedApps"
            :key="app.appId"
            class="app-item"
            :class="{ 'border-top': index > 0 }"
          >
            <div class="app-icon-wrapper icon-grayscale">
              <img :src="appIcon" :alt="app.appName" class="app-icon-img" />
            </div>
            <div class="app-info">
              <div class="app-name" :title="app.appName">{{ app.appName || '-' }}</div>
            </div>
            <div class="app-actions">
              <button
                class="action-btn resume"
                @click="handleResume(app)"
                :disabled="actionLoading === app.appId"
              >
                {{ $t('controlCenter.appManagement.actions.resume') }}
              </button>
              <button
                class="action-btn deprovision"
                @click="handleDeprovision(app)"
                :disabled="actionLoading === app.appId"
              >
                {{ $t('controlCenter.appManagement.actions.deprovision') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Not Provisioned 应用 -->
      <div class="app-section">
        <h2 class="section-title">{{ $t('controlCenter.appManagement.section.notProvisioned') }}</h2>
        <div v-if="inactiveApps.length === 0" class="empty-section">
          <p>{{ $t('controlCenter.appManagement.empty') }}</p>
        </div>
        <div v-else class="app-list">
          <div
            v-for="(app, index) in inactiveApps"
            :key="app.appId"
            class="app-item"
            :class="{ 'border-top': index > 0 }"
          >
            <div class="app-icon-wrapper icon-grayscale">
              <img :src="appIcon" :alt="app.appName" class="app-icon-img" />
            </div>
            <div class="app-info">
              <div class="app-name" :title="app.appName">{{ app.appName || '-' }}</div>
            </div>
            <div class="app-actions">
              <button
                class="action-btn provision"
                @click="handleProvision(app)"
                :disabled="actionLoading === app.appId"
              >
                {{ $t('controlCenter.appManagement.actions.provision') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Deprovision Confirmation Dialog -->
    <el-dialog
      :title="$t('controlCenter.appManagement.dialogs.deprovision.title')"
      :visible.sync="cancelDialogVisible"
      width="400px"
    >
      <p>{{ $t('controlCenter.appManagement.dialogs.deprovision.message', { appName: currentApp?.appName }) }}</p>
      <span slot="footer" class="dialog-footer">
        <button class="btn" @click="cancelDialogVisible = false">
          {{ $t('controlCenter.common.cancel') }}
        </button>
        <button class="btn btn-danger" @click="confirmDeprovision" :disabled="actionLoading">
          {{ $t('controlCenter.appManagement.dialogs.deprovision.confirm') }}
        </button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { iamApi } from '@/api/iam'
import { getUserInfo } from '@/utils/auth'

export default {
  name: 'AppManagement',
  data() {
    return {
      loading: false,
      actionLoading: null,
      apps: [],
      appIcon: require('@/assets/app_icon.jpg'),
      cancelDialogVisible: false,
      currentApp: null
    }
  },
  computed: {
    accountId() {
      const userInfo = getUserInfo()
      return userInfo?.accountId || userInfo?.sub || userInfo?.userId || 'default'
    },
    activeApps() {
      return this.apps.filter(app => app.status === 'ACTIVE')
    },
    pausedApps() {
      return this.apps.filter(app => app.status === 'SUSPENDED')
    },
    inactiveApps() {
      return this.apps.filter(app => app.status === 'NOT_ACTIVATED')
    }
  },
  created() {
    this.fetchApps()
  },
  methods: {
    async fetchApps() {
      this.loading = true
      try {
        const response = await iamApi.getApplicationPanel(this.accountId)
        const appsData = Array.isArray(response) ? response : response.data
        this.apps = appsData.map(app => ({
          appId: app.code.toLowerCase(),
          appName: app.name,
          appCode: app.code,
          status: app.status
        }))
      } catch (error) {
        console.error('Failed to fetch apps:', error)
        this.$message.error(this.$t('controlCenter.appManagement.loadError'))
      } finally {
        this.loading = false
      }
    },
    async handleProvision(app) {
      this.actionLoading = app.appId
      try {
        await iamApi.provisionApp(this.accountId, app.appCode)
        this.$message.success(this.$t('controlCenter.appManagement.messages.provisionSuccess'))
        this.fetchApps()
      } catch (error) {
        console.error('Failed to provision app:', error)
        this.$message.error(this.$t('controlCenter.appManagement.messages.provisionError'))
      } finally {
        this.actionLoading = null
      }
    },
    async handleSuspend(app) {
      this.actionLoading = app.appId
      try {
        await iamApi.suspendApp(this.accountId, app.appCode)
        this.$message.success(this.$t('controlCenter.appManagement.messages.suspendSuccess'))
        this.fetchApps()
      } catch (error) {
        console.error('Failed to suspend app:', error)
        this.$message.error(this.$t('controlCenter.appManagement.messages.suspendError'))
      } finally {
        this.actionLoading = null
      }
    },
    async handleResume(app) {
      this.actionLoading = app.appId
      try {
        await iamApi.resumeApp(this.accountId, app.appCode)
        this.$message.success(this.$t('controlCenter.appManagement.messages.resumeSuccess'))
        this.fetchApps()
      } catch (error) {
        console.error('Failed to resume app:', error)
        this.$message.error(this.$t('controlCenter.appManagement.messages.resumeError'))
      } finally {
        this.actionLoading = null
      }
    },
    handleDeprovision(app) {
      this.currentApp = app
      this.cancelDialogVisible = true
    },
    async confirmDeprovision() {
      if (!this.currentApp) return
      this.actionLoading = this.currentApp.appId
      try {
        await iamApi.deprovisionApp(this.accountId, this.currentApp.appCode)
        this.$message.success(this.$t('controlCenter.appManagement.messages.deprovisionSuccess'))
        this.cancelDialogVisible = false
        this.fetchApps()
      } catch (error) {
        console.error('Failed to deprovision app:', error)
        this.$message.error(this.$t('controlCenter.appManagement.messages.deprovisionError'))
      } finally {
        this.actionLoading = null
        this.currentApp = null
      }
    }
  }
}
</script>

<style scoped>
.app-management {
  padding: 20px;
}

.main-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.main-header h1 {
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 4px 0;
}

.subtitle {
  color: #6b7280;
  margin: 0;
  font-size: 14px;
}

.app-sections {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.app-section {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 16px 0;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
}

.empty-section {
  text-align: center;
  padding: 20px;
  color: #9ca3af;
}

.app-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 20px;
}

.app-list {
  display: flex;
  flex-direction: column;
}

.app-item {
  display: flex;
  align-items: center;
  padding: 16px 0;
}

.app-item.border-top {
  border-top: 1px solid #e5e7eb;
}

.app-item:first-child {
  padding-top: 0;
}

.app-item:last-child {
  padding-bottom: 0;
}

.app-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 12px 12px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  position: relative;
}

.app-card:hover {
}

.app-icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  flex-shrink: 0;
  margin-right: 16px;
}

.app-icon-wrapper.icon-grayscale {
  background: linear-gradient(135deg, #d1d5db 0%, #9ca3af 100%);
  box-shadow: none;
  filter: grayscale(100%);
  opacity: 0.7;
}

.app-icon-fallback {
  font-size: 28px;
  color: #fff;
}

.app-icon-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.app-info {
  flex: 1;
  min-width: 0;
}

.app-name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.app-actions {
  display: flex;
  gap: 16px;
  flex-shrink: 0;
}

.action-btn {
  padding: 4px 10px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
}

.action-btn:hover:not(:disabled) {
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.action-btn.provision {
  background: transparent;
  color: #2563eb;
  text-decoration: underline;
}

.action-btn.suspend {
  background: transparent;
  color: #2563eb;
  text-decoration: underline;
}

.action-btn.resume {
  background: transparent;
  color: #2563eb;
  text-decoration: underline;
}

.action-btn.deprovision {
  background: transparent;
  color: #2563eb;
  text-decoration: underline;
}

.action-btn.deprovision:hover:not(:disabled) {
}

.btn {
  padding: 8px 16px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.btn-primary {
  background: #3b82f6;
  color: #fff;
}

.btn-primary:hover {
  background: #2563eb;
}

.btn-danger {
  background: #ef4444;
  color: #fff;
}

.btn-danger:hover {
  background: #dc2626;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

@media (max-width: 768px) {
  .app-management {
    padding: 0;
  }

  .main-header {
    flex-direction: column;
    gap: 12px;
  }

  .main-header .btn {
    width: 100%;
    justify-content: center;
  }

  .app-section {
    padding: 16px;
  }

  .app-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 12px;
  }

  .app-item {
    display: grid;
    grid-template-columns: 56px minmax(0, 1fr);
    align-items: flex-start;
    gap: 12px;
    padding: 14px 0;
  }

  .app-icon-wrapper {
    width: 56px;
    height: 56px;
    margin-right: 0;
  }

  .app-name {
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .app-actions {
    grid-column: 1 / -1;
    width: 100%;
    gap: 8px;
    flex-wrap: wrap;
  }

  .action-btn {
    flex: 1 1 120px;
    min-height: 34px;
    border: 1px solid #dbeafe;
    background: #eff6ff !important;
    text-decoration: none;
  }
}

@media (max-width: 420px) {
  .app-actions {
    flex-direction: column;
  }

  .action-btn {
    width: 100%;
  }
}
</style>
